import {
    Column,
    Container,
    Image,
    MaterialContext,
    Panel,
    Panel9Slice,
    ProgressBar,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {Align, FlexDirection, Justify, PositionType} from '@wonderlandengine/react-ui';

import {useState, useEffect, useMemo, useContext, useRef} from 'react';
import {RotBMaterialContext} from '../utils/rotb-material-context.js';
import {ScoreController} from '../../components/score-controller.js';
import {PowerupManager} from '../../components/powerup-manager.js';
import {formatNumber} from '../../utils/Utils.js';
import {colorSwatch} from '../utils/colorSwatch.js';
import {PowerupButton} from '../components/powerup-button.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {RotatableImage} from '../components/rotatable-image.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {loop, stop} from '../utils/animation-context.js';
import {Object3D} from '@wonderlandengine/api';
import {quat} from 'gl-matrix';
import {pauseButton} from '../utils/pauseButton.js';

const tempPosLength = 32;
const tempPositions = new Float32Array(3 * tempPosLength);

export const HUDPanel = () => {
    const rotbMats = useContext(RotBMaterialContext);
    const HUDModel = ScoreController.instance.HUDModel;
    const mat = useContext(MaterialContext);

    const scoreBarMaterial = useMemo(() => rotbMats?.hudBarMaterial, []);

    const [score, setScore] = useState(HUDModel.score);
    const [level, setLevel] = useState(HUDModel.level);
    const [coins, setCoins] = useState(HUDModel.coins);
    const [sliderValue, setSliderValue] = useState(HUDModel.sliderValue);

    const [powerup1, setPowerup1] = useState(PowerupManager.instance.ingameSlot1);
    const [powerup2, setPowerup2] = useState(PowerupManager.instance.ingameSlot2);
    const [powerup3, setPowerup3] = useState(PowerupManager.instance.ingameSlot3);
    const [powerup4, setPowerup4] = useState(PowerupManager.instance.ingameSlot4);

    const [showGameOverText, setShowGameOverText] = useState(HUDModel.showGameOver);
    const [showMaxedLevelText, setShowMaxedLevelText] = useState(HUDModel.showMaxedLevel);
    const [readySetGoText, setReadySetGoText] = useState(HUDModel.readySetGoText);
    const [lastWaveText, setLastWaveText] = useState(HUDModel.lastWaveText);

    const [showReadySetGoText, setShowReadySetGoText] = useState(true);
    const [showLastWaveText, setShowLastWaveText] = useState(false);
    const [tachoRotation, setTachoRotation] = useState(HUDModel.tachometerValue);
    const [starCounter, setStarCounter] = useState(HUDModel.starCounter);
    const [currentGameMode, setCurrentGameMode] = useState(HUDModel.gameMode);

    useEffect(() => {
        const onScoreModelChanged = (property: string) => {
            switch (property) {
                case 'score':
                    setScore(HUDModel.score);
                    break;
                case 'showGameOver':
                    setShowGameOverText(HUDModel.showGameOver);
                    break;
                case 'showMaxedLevel':
                    setShowMaxedLevelText(HUDModel.showMaxedLevel);
                    setShowLastWaveText(false);
                    break;
                case 'level':
                    setLevel(HUDModel.level);
                    break;
                case 'coins':
                    setCoins(HUDModel.coins);
                    break;
                case 'sliderValue':
                    setSliderValue(HUDModel.sliderValue);
                    break;
                case 'readySetGoText':
                    setShowReadySetGoText(true);
                    setReadySetGoText(HUDModel.readySetGoText);
                    break;
                case 'lastWaveText':
                    setShowLastWaveText(HUDModel.lastWaveText.length > 0);
                    setLastWaveText(HUDModel.lastWaveText);
                    if (HUDModel.lastWaveText.length > 0) {
                        setTimeout(() => {
                            setShowLastWaveText(false);
                        }, 6000);
                    }
                    break;
                case 'tachometerValue':
                    setTachoRotation(HUDModel.tachometerValue);
                    break;
                case 'gameMode':
                    setCurrentGameMode(HUDModel.gameMode);
                    break;
                case 'starCounter':
                    setStarCounter(HUDModel.starCounter);
                    break;
                default:
                    break;
            }
        };
        HUDModel.propertyChanged.add(onScoreModelChanged);

        return () => {
            HUDModel.propertyChanged.remove(onScoreModelChanged);
        };
    });

    const arrowRef = useRef<Object3D>(null);
    /* Play animation if requested */
    useEffect(() => {
        if (currentGameMode != GameModeType.Easy) return;
        const tempRotation = new Float32Array(4);
        if (!arrowRef.current) return;
        const q = arrowRef.current.findByNameRecursive('mesh')[0];
        const anim = loop((f) => {
            if (!arrowRef.current || !q || q.isDestroyed) return;
            const a = 0.01;
            const x = a * Math.sin(f * 4 * Math.PI);
            q.getRotationLocal(tempRotation);
            quat.rotateZ(tempRotation, tempRotation, x);
            q.setRotationLocal(tempRotation);
        }, 0.4);
        return () => stop(anim);
    }, [currentGameMode]);

    return (
        <Container
            margin={0}
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Column}
            justifyContent={Justify.FlexStart}
        >
            <Row flexGrow={0}>
                <Container width="100%">
                    {!showGameOverText && !showMaxedLevelText && (
                        <Panel
                            backgroundColor={colorSwatch.HudBarBackground}
                            paddingTop={8}
                            paddingBottom={4}
                            rounding={0}
                            width="100%"
                        >
                            <Panel9Slice
                                material={scoreBarMaterial}
                                borderSize={5}
                                borderTextureSize={9 / 64}
                                height={70}
                                width="100%"
                            >
                                <Container
                                    alignItems={Align.Center}
                                    height={70}
                                    flexDirection={FlexDirection.Row}
                                    justifyContent={Justify.SpaceBetween}
                                    alignContent={Align.Center}
                                    alignSelf={Align.Center}
                                    width="100%"
                                    maxWidth={1000}
                                >
                                    <Column height={70} paddingTop={10} paddingLeft={20}>
                                        <Text
                                            height={35}
                                            fontSize={20}
                                            color={colorSwatch.MalwearCircle}
                                        >
                                            {`Coins: ${coins}`}
                                        </Text>
                                        <Text
                                            fontSize={25}
                                            color={colorSwatch.HudLevelLabel}
                                        >
                                            {level}
                                        </Text>
                                    </Column>
                                    <Column
                                        maxWidth={500}
                                        alignItems={Align.Stretch}
                                        flexGrow={1}
                                        flexShrink={1}
                                        gap={10}
                                    >
                                        <Text
                                            top={15}
                                            height={40}
                                            textAlign="center"
                                            fontSize={32}
                                        >
                                            {formatNumber(score)}
                                        </Text>
                                        <ProgressBar
                                            fgColor={colorSwatch.HudLevelBar}
                                            bgColor={colorSwatch.HudLevelBackground}
                                            // width="100%"
                                            padding={0}
                                            paddingTop={4}
                                            paddingBottom={4}
                                            paddingLeft={4}
                                            value={sliderValue}
                                            height={20}
                                            rounding={0}
                                            border={0}
                                        ></ProgressBar>
                                    </Column>
                                    {/* <Column flexGrow={0} flexShrink={0} minWidth={160}>

                                </Column> */}
                                    <Column flexGrow={0} flexShrink={0} width={200}>
                                        <Row width={200} justifyContent={Justify.FlexEnd}>
                                            {currentGameMode === GameModeType.Easy && (
                                                <Container
                                                    position={PositionType.Absolute}
                                                    top={35}
                                                    right={20}
                                                    width={150}
                                                    height={150}
                                                >
                                                    <Image
                                                        src={
                                                            UiTexturesManager.instance
                                                                .tachometer
                                                        }
                                                        width={150}
                                                        height={150}
                                                    >
                                                        <RotatableImage
                                                            ref={arrowRef}
                                                            width={150}
                                                            height={150}
                                                            src={
                                                                UiTexturesManager.instance
                                                                    .tachometerArrow
                                                            }
                                                            rotation={tachoRotation}
                                                        ></RotatableImage>
                                                    </Image>
                                                </Container>
                                            )}
                                            {currentGameMode === GameModeType.Quickplay && (
                                                <Container
                                                    position={PositionType.Absolute}
                                                    top={35}
                                                    right={20}
                                                    width={150}
                                                    height={150}
                                                >
                                                    <Image
                                                        src={
                                                            UiTexturesManager.instance
                                                                .starIcon
                                                        }
                                                        width={150}
                                                        height={150}
                                                        justifyContent={Justify.Center}
                                                        alignItems={Align.Center}
                                                    >
                                                        <Text
                                                            z={0.001}
                                                            textAlign="center"
                                                            fontSize={32}
                                                            color={
                                                                colorSwatch.StartCounterText
                                                            }
                                                            text={`${starCounter}`}
                                                        ></Text>
                                                    </Image>
                                                </Container>
                                            )}

                                            <Container
                                                width={70}
                                                height={70}
                                                onClick={pauseButton}
                                                justifyContent={Justify.Center}
                                                alignItems={Align.Center}
                                                border={0}
                                            >
                                                <Image
                                                    rounding={0}
                                                    src={
                                                        UiTexturesManager.instance.pauseIcon
                                                    }
                                                    width={35}
                                                    height={35}
                                                ></Image>
                                            </Container>
                                        </Row>
                                    </Column>
                                </Container>
                            </Panel9Slice>
                        </Panel>
                    )}
                </Container>
            </Row>
            <Row
                width="100%"
                maxWidth={1000}
                alignSelf={Align.Center}
                flexGrow={1}
                padding={50}
            >
                <Container
                    width={100}
                    flexGrow={0}
                    justifyContent={Justify.FlexEnd}
                    gap={50}
                >
                    <PowerupButton powerUp={powerup4}></PowerupButton>
                    <PowerupButton powerUp={powerup2}></PowerupButton>
                </Container>
                <Container
                    flexGrow={1}
                    height="75%"
                    flexDirection={FlexDirection.Column}
                    justifyContent={Justify.Center}
                    alignItems={Align.Center}
                >
                    {showReadySetGoText && (
                        <Container>
                            <Text
                                textAlign="center"
                                material={rotbMats?.uiTextShadowMaterial}
                                fontSize={52}
                            >
                                {readySetGoText}
                            </Text>
                        </Container>
                    )}
                    {showLastWaveText && (
                        <Text
                            textAlign="center"
                            material={rotbMats?.uiTextShadowMaterial}
                            fontSize={52}
                        >
                            {lastWaveText}
                        </Text>
                    )}
                    {showGameOverText && (
                        <Text
                            textAlign="center"
                            material={rotbMats?.uiTextShadowMaterial}
                            fontSize={52}
                        >
                            GAME OVER!
                        </Text>
                    )}
                    {showMaxedLevelText && (
                        <Text
                            textAlign="center"
                            material={rotbMats?.uiTextShadowMaterial}
                            fontSize={52}
                        >
                            WINNER!
                        </Text>
                    )}
                </Container>
                <Container
                    width={100}
                    flexGrow={0}
                    justifyContent={Justify.FlexEnd}
                    gap={50}
                >
                    <PowerupButton powerUp={powerup3}></PowerupButton>
                    <PowerupButton powerUp={powerup1}></PowerupButton>
                </Container>
            </Row>
            <Row flexGrow={0}>
                {/* <Container width="100%">
                    <Text>Bottom</Text>
                </Container> */}
            </Row>
        </Container>
    );
};
